import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonProgressBar,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { add } from 'ionicons/icons';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { useContactState } from '@/modules/contact/ContactStateContext';
import { ContactLogic, type Contact } from '@/modules/contact';
import {
  selectAllContacts,
  selectAllListingCategories,
  selectContactsByListingCategory,
} from '@/modules/contact/reducer';
import SearchableContactList from '@/components/SearchableContactList';
import {
  useContactPicker,
  type DeviceContactSelection,
} from '@/contexts/ContactPickerContext';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import ContactsInterstitial from '@/modules/topic/ContactsInterstitial';
import RecipientBox from './RecipientBox';
import { useScreenfulAwareness } from './useScreenfulAwareness';
import '@/components/Interstitial.css';
import {
  pxToRem,
  footerControlRoundButtonStyle,
} from '@/modules/config/constants';

interface RecipientPickerBodyProps {
  /**
   * The advance button's label. The surface above owns it because it is the
   * only thing about the picker that reads differently per flow — the wizard
   * step says "Next (3 selected)" where the garden modal says
   * "Start Drafting · 3 people" — and it already knows the count.
   */
  ctaLabel: string;
  /** Where the advance button goes. Only called while the selection can advance. */
  onAdvance: () => void;
  /** The advance button's test id, which each surface names for itself. */
  ctaTestId: string;
}

/**
 * The picking surface: who is being messaged, who can be picked, and the one
 * button that leaves with them. Everything here is about the selection and
 * nothing about the surface around it, so a routed wizard step and a modal can
 * each wrap it in their own frame and get the same picker.
 *
 * Recipient state comes from ShareWizardContext, which both surfaces mount
 * under; only the CTA arrives as a prop.
 */
const RecipientPickerBody: React.FC<RecipientPickerBodyProps> = ({
  ctaLabel,
  onAdvance,
  ctaTestId,
}) => {
  const {
    recipients,
    contactIntents,
    introBlockedKeys,
    confirmedContactIds,
    isRecipientBoxExpanded,
    setIsRecipientBoxExpanded,
    handleAddRecipients,
    handleAddRecipientsProvisionally,
    handleRemoveRecipient,
    handleRequestAddContact,
    inProgressLegId,
    recipientMatchProgress,
    actionAwarenessMap,
    recipientsAwarenessChecked,
    excludeAwareContacts,
    setExcludeAwareContacts,
    isUpdateFlow,
    requestAwarenessFor,
  } = useShareWizardContext();
  const { state, isLoading: contactsLoading } = useContactState();
  const { DeviceContactPicker } = useContactPicker();
  const [presentToast] = useAppToast();
  const router = useIonRouter();
  const { isSuppressed, loaded: interstitialsLoaded } =
    useSuppressibleNotices();
  const [showContactsInterstitial, setShowContactsInterstitial] =
    useState(false);
  // Whether the page holding the picker is stacked under another view. The
  // picker can mount there — its step unlocks while the sender sets their name
  // on the profile pushed over it — and the import nudge, a sheet presented at
  // the app root, would then open over that unrelated page. It waits until the
  // page is shown.
  const rootRef = useRef<HTMLDivElement>(null);
  const [isPageHidden, setIsPageHidden] = useState(false);
  useLayoutEffect(() => {
    setIsPageHidden(rootRef.current?.closest('.ion-page-hidden') != null);
  }, []);
  useIonViewDidEnter(() => setIsPageHidden(false));
  useIonViewWillLeave(() => setIsPageHidden(true));
  // The contact list's own scroll region, held as state (not a ref) so the
  // screenful watcher below re-arms when it mounts.
  const [listScrollEl, setListScrollEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    // Only nudge people to import contacts when their address book is empty —
    // if they already have any contacts at all, skip the interstitial. Wait for
    // contacts to finish loading so the count is reliable before deciding.
    if (interstitialsLoaded && !contactsLoading) {
      setShowContactsInterstitial(
        !isSuppressed('contacts') && state.allIds.length === 0
      );
    }
    // Run once when the suppressed state and contacts finish loading; isSuppressed
    // and state are intentionally excluded so neither a same-session dismiss nor a
    // contact added on this screen re-opens the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interstitialsLoaded, contactsLoading]);

  const allContacts = useMemo(() => selectAllContacts(state), [state]);
  const listingCategories = useMemo(
    () => selectAllListingCategories(state),
    [state]
  );
  // Contacts with no US phone can't be SMS'd through the US-only sharing pipeline.
  // They're shown but disabled (with an explanatory badge) so picking one can't
  // hang the flow later.
  const unreachableIds = useMemo(
    () =>
      allContacts
        .filter(contact => !ContactLogic.isReachable(contact))
        .map(contact => contact.localId),
    [allContacts]
  );
  // Contacts already introduced to this topic, as far as awareness has resolved
  // so far (#581). Only meaningful while the exclude box is checked: otherwise
  // they are ordinary recipients who happen to route to a reminder.
  const awareIds = useMemo(
    () =>
      excludeAwareContacts
        ? allContacts
            .filter(contact => {
              const userId = contact.snowballId;
              return (
                !!userId && !!actionAwarenessMap.get(userId)?.introducedByAnyone
              );
            })
            .map(contact => contact.localId)
        : [],
    [allContacts, actionAwarenessMap, excludeAwareContacts]
  );
  const disabledContactIds = useMemo(
    () =>
      inProgressLegId != null
        ? allContacts.map(contact => contact.localId)
        : [...unreachableIds, ...awareIds],
    [allContacts, awareIds, inProgressLegId, unreachableIds]
  );
  // Why each disabled row is disabled. Unreachable wins where both apply: no
  // phone number blocks the send outright, so it's the more useful thing to say.
  const disabledNotices = useMemo(() => {
    const notices: Record<string, string> = {};
    for (const localId of awareIds) notices[localId] = 'Already introduced';
    for (const localId of unreachableIds) notices[localId] = 'No U.S. phone';
    return notices;
  }, [awareIds, unreachableIds]);

  // Resolve awareness for the rows actually on screen (#581) — the whole
  // address book would cost an OPRF hash and a round trip per contact. Only
  // needed while the exclude box is checked, which is what awareness decides
  // here; unchecked, an aware contact is just a reminder recipient and the
  // recipient-driven check in ShareWizardContext already covers them.
  useScreenfulAwareness({
    container: excludeAwareContacts ? listScrollEl : null,
    contacts: allContacts,
    onScreenful: requestAwarenessFor,
  });
  /**
   * Why the selection can't be advanced, or null when it can.
   *
   * A picked recipient is not necessarily a messageable one: contactIntents
   * holds only those an intent actually routes to, dropping anyone already
   * messaged about this topic, blocked by the share limit, or confirmed in an
   * earlier leg. Advancing on `recipients.length` alone carries an all-blocked
   * selection into compose, where the send path has nothing to leg and the CTA
   * there goes dead.
   */
  const blockedReason = useMemo(() => {
    if (state.duplicatesToResolve.length > 0) return null; // its own UI handles this
    if (recipients.length === 0) return null; // the empty CTA label says enough
    if (contactIntents.size > 0) return null;
    return 'Everyone you picked has already been messaged about this topic, or the share limit was reached.';
  }, [
    state.duplicatesToResolve.length,
    recipients.length,
    contactIntents.size,
  ]);

  // Each recipient's intent comes from awareness, so until the check has
  // answered for the people picked, someone who already knows would be routed
  // as new.
  const canAdvance =
    recipients.length > 0 &&
    contactIntents.size > 0 &&
    state.duplicatesToResolve.length === 0 &&
    recipientsAwarenessChecked;

  // Ingestion handler for device-picked contacts: route each into the recipient
  // list — reusing an existing address-book entry where one matches, adding the
  // rest provisionally — and warn about skipped/duplicate entries.
  const handleDeviceContacts = useCallback(
    ({
      contacts: deduped,
      noSmsableCount,
      droppedPhoneCount,
    }: DeviceContactSelection) => {
      if (inProgressLegId != null) return;

      let alreadySelectedCount = 0;

      const selectedContactIds = state.recipientIds;
      const selectedSet = new Set(selectedContactIds);

      const notYetSelected: Contact[] = [];
      for (const newContact of deduped) {
        const alreadySelected = selectedContactIds.some(id => {
          const contact = state.byId[id];
          if (!contact) return false;
          return (
            ContactLogic.fullName(contact) ===
              ContactLogic.fullName(newContact) &&
            ContactLogic.hasPhoneSubsetOf(newContact, contact)
          );
        });

        if (alreadySelected) {
          alreadySelectedCount++;
        } else {
          notYetSelected.push(newContact);
        }
      }

      const substituteContactsFromAddressBook: Contact[] = [];
      const newContacts: Contact[] = [];
      for (const newContact of notYetSelected) {
        const substitute = Object.values(state.byId).find(contact => {
          if (selectedSet.has(contact.localId)) return false;
          return (
            ContactLogic.fullName(contact) ===
              ContactLogic.fullName(newContact) &&
            ContactLogic.hasPhoneSubsetOf(newContact, contact)
          );
        });

        if (substitute) {
          substituteContactsFromAddressBook.push(substitute);
        } else {
          newContacts.push(newContact);
        }
      }

      const warnings: string[] = [];
      if (noSmsableCount > 0) {
        warnings.push(
          `Skipped ${noSmsableCount} contact${noSmsableCount > 1 ? 's' : ''} with no SMS-able phone numbers`
        );
      }
      if (droppedPhoneCount > 0) {
        warnings.push(
          `Skipped ${droppedPhoneCount} duplicate phone number${droppedPhoneCount > 1 ? 's' : ''} (a common issue with PWA contact pickers)`
        );
      }
      if (alreadySelectedCount > 0) {
        warnings.push(
          `Skipped ${alreadySelectedCount} contact${alreadySelectedCount > 1 ? 's' : ''} already listed as recipients`
        );
      }

      if (substituteContactsFromAddressBook.length + newContacts.length > 0) {
        handleAddRecipients(substituteContactsFromAddressBook);
        handleAddRecipientsProvisionally(newContacts);
        if (warnings.length > 0) {
          presentToast({
            message: warnings.join('<br>'),
            duration: 3000,
            color: 'warning',
          });
        }
      } else {
        presentToast({
          message:
            warnings.length > 0
              ? warnings.join('<br>')
              : 'No new valid contacts selected',
          duration: 3000,
          color: 'warning',
        });
      }
    },
    [
      handleAddRecipients,
      handleAddRecipientsProvisionally,
      inProgressLegId,
      presentToast,
      state.byId,
      state.recipientIds,
    ]
  );

  const handleAddContactClick = useCallback(() => {
    if (inProgressLegId != null) return;
    handleRequestAddContact('', contact => {
      handleAddRecipients([contact]);
    });
  }, [handleAddRecipients, handleRequestAddContact, inProgressLegId]);

  const handleListingCategoryClick = useCallback(
    (category: string) => {
      if (inProgressLegId != null) return;
      const selectedIds = new Set(state.recipientIds);
      const contacts = selectContactsByListingCategory(state, category).filter(
        contact =>
          !selectedIds.has(contact.localId) && ContactLogic.isReachable(contact)
      );
      if (contacts.length > 0) {
        handleAddRecipients(contacts);
      }
    },
    [handleAddRecipients, inProgressLegId, state]
  );

  const footerElemWhenNotSearching = (
    <div
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flex: '0 0 auto',
      }}
    >
      <IonButton
        fill="clear"
        onClick={handleAddContactClick}
        aria-label="Add contact"
        data-testid="add-contact-button"
        disabled={inProgressLegId != null}
        className="footer-glass-button footer-glass-round"
        style={
          {
            ...footerControlRoundButtonStyle,
            width: 44,
            height: 44,
            '--border-radius': '50%',
            flex: '0 0 44px',
          } as React.CSSProperties
        }
      >
        <IonIcon
          slot="icon-only"
          icon={add}
          style={{ fontSize: pxToRem(28) }}
        />
      </IonButton>
      <DeviceContactPicker
        onSelect={handleDeviceContacts}
        disabled={inProgressLegId != null}
      />
    </div>
  );

  return (
    <>
      {/* Fill the available height as a flex column so the contact list scrolls
          within its own region rather than the whole surface. Otherwise the
          surface's scrollbar and the expanded recipient box's own (capped,
          scrollable) box stack up at the top-right as a confusing double
          scrollbar. Bounding the contact region keeps its scrollbar clear of
          the recipient box's. */}
      <div
        ref={rootRef}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 12,
            flex: '0 0 auto',
            padding: '0 20px 12px',
            background: 'var(--app-page-background)',
          }}
        >
          <RecipientBox
            to="To"
            value={[...state.recipientIds].reverse()}
            onRemove={reversedIndex =>
              handleRemoveRecipient(
                state.recipientIds.length - 1 - reversedIndex
              )
            }
            contactIntents={contactIntents}
            introBlockedKeys={introBlockedKeys}
            confirmedContactIds={confirmedContactIds}
            isExpandedOnMount={isRecipientBoxExpanded}
            onExpandedChange={setIsRecipientBoxExpanded}
            disabled={inProgressLegId != null}
          />
          {/* Whether prior awareness of this topic disqualifies someone or
              just changes the message they get (#581). The entry point seeds
              it: a reshare starts it on, because passing the story to someone
              who already has it is pointless, and an ordinary share takes the
              topic type's own opening position. Aware contacts are disabled
              in the list below while it is checked.

              The update flow does not offer it at all: that flow exists to
              tell people who already know what has changed, so excluding
              them would empty it. */}
          {!isUpdateFlow && (
            <IonCheckbox
              checked={excludeAwareContacts}
              disabled={inProgressLegId != null}
              labelPlacement="end"
              justify="start"
              style={{ marginTop: 8 }}
              data-testid="exclude-aware-checkbox"
              onIonChange={event =>
                setExcludeAwareContacts(event.detail.checked)
              }
            >
              <span style={{ fontSize: pxToRem(13), whiteSpace: 'normal' }}>
                Exclude recipients who&apos;ve seen this topic before
              </span>
            </IonCheckbox>
          )}
          {recipientMatchProgress && (
            <div
              style={{ marginTop: 8 }}
              data-testid="recipient-match-progress"
              role="status"
              aria-live="polite"
            >
              <div
                style={{
                  fontSize: pxToRem(13),
                  color: 'var(--ion-color-medium)',
                  marginBottom: 6,
                }}
              >
                Finding people you've shared with before…
              </div>
              <IonProgressBar
                value={
                  recipientMatchProgress.total > 0
                    ? recipientMatchProgress.done / recipientMatchProgress.total
                    : 0
                }
              />
            </div>
          )}
        </div>
        <div
          ref={setListScrollEl}
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: 16,
          }}
        >
          <SearchableContactList
            contacts={allContacts}
            disabledContacts={disabledContactIds}
            disabledNotices={disabledNotices}
            selectedContacts={state.recipientIds}
            onContactClick={contact => {
              if (inProgressLegId == null) handleAddRecipients([contact]);
            }}
            onContactDeselect={contact => {
              if (inProgressLegId == null) {
                handleRemoveRecipient(
                  state.recipientIds.indexOf(contact.localId)
                );
              }
            }}
            listingCategoriesToSearch={listingCategories}
            onListingCategoryClick={handleListingCategoryClick}
            actionRow={
              <>
                {blockedReason && (
                  <div
                    role="status"
                    data-testid="share-wizard-next-blocked"
                    style={{
                      padding: '0 8px 8px',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      color: '#666',
                    }}
                  >
                    {blockedReason}
                  </div>
                )}
                <IonButton
                  expand="block"
                  color="primary"
                  style={{ margin: 0 }}
                  data-testid={ctaTestId}
                  disabled={!canAdvance || inProgressLegId != null}
                  onClick={onAdvance}
                >
                  {ctaLabel}
                </IonButton>
              </>
            }
            footerElemWhenNotSearching={footerElemWhenNotSearching}
            sparseHint={
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                <p>
                  Current address book status: <strong>Ghost town</strong>
                </p>
                <p style={{ marginTop: 8 }}>
                  You can{' '}
                  <button
                    className="interstitial__link"
                    onClick={() =>
                      router.push(
                        '/settings/contacts/import',
                        'forward',
                        'push'
                      )
                    }
                  >
                    import entries
                  </button>{' '}
                  from your Contacts app into Snowball (encrypted for your eyes
                  only on our servers).
                </p>
              </div>
            }
          />
        </div>
      </div>
      <ContactsInterstitial
        isOpen={showContactsInterstitial && !isPageHidden}
        onDismiss={() => setShowContactsInterstitial(false)}
      />
    </>
  );
};

export default RecipientPickerBody;
